import * as api from './api';

// Watches a set of sheets for new rows on behalf of every Recent Submissions widget showing them. The widgets on a
// dashboard are all handed the same sheets and the same filters, so each watching for itself would ask the API the
// same thing once per widget. Instead the first widget to subscribe starts a watcher, the rest share it, and it stops
// once the last one leaves. What each widget does with the rows - picking out its question's answers, applying its
// filter - stays with the widget.
//
// This is meant to be watched while data is coming in - a scale weighing, for example - so a submission should appear
// within a few seconds of being taken rather than whenever the page is next loaded. Each check asks for a change token
// per sheet, which loads no rows and so is cheap enough to ask for often; the rows themselves are only fetched for the
// sheets whose token moved, which on a quiet dashboard is none of them.
export const CHECK_INTERVAL = 5000;
// How often every sheet is swept, rather than only the ones being watched closely. This is what picks up a
// submission on a sheet that had gone quiet, or one reopened after being completed, and once it does the sheet
// joins the close watch on its own.
export const SWEEP_INTERVAL = 30000;
// How long to wait before checking again after a failed check, so an API outage is not hit every check.
export const ERROR_INTERVAL = 5000;
// How often the "Updated ..." time is allowed to move while nothing is changing. Without this every widget would
// re-render each check just to re-word the same status.
export const STATUS_INTERVAL = 15000;
export const PAGE_SIZE = 50;

const INITIAL_STATUS = {failed: false, stopped: false, lastUpdate: null};

// Running watchers, by what they watch.
const watchers = {};

// Starts watching sheetIds, or joins the watcher already doing so. watchedSheetIds are the ones asked about every
// check rather than only on a sweep. The listener's onRows is given [{sheetId, content}] with the newest page of rows
// of each sheet that changed, and onStatus is given {failed, stopped, lastUpdate} whenever that changes. A listener
// joining a watcher that is already running is given the rows and status it has so far straight away. Returns a
// function that unsubscribes.
export function watchSheetRows({sheetIds, watchedSheetIds, filters, credentials}, listener) {
  const key = JSON.stringify([credentials, filters || null, sheetIds]);
  let watcher = watchers[key];

  if (!watcher) {
    watcher = watchers[key] = createWatcher(key, sheetIds, filters, credentials);
    const unsubscribe = watcher.subscribe(watchedSheetIds, listener);
    watcher.start();
    return unsubscribe;
  }

  return watcher.subscribe(watchedSheetIds, listener);
}

function createWatcher(key, sheetIds, filters, credentials) {
  const listeners = [];
  // The change token each sheet was last seen at. A sheet whose token has not moved has no new rows to fetch.
  const versionsBySheetId = {};
  // The newest page of rows fetched for each sheet, so a widget that joins late starts from what the others have.
  const pagesBySheetId = {};
  let watchedSheetIds = [];
  // Sheets whose part of the last check failed. Their tokens are unknown rather than unchanged, so they are asked
  // about again on the next check instead of waiting for the next sweep.
  let retrySheetIds = [];
  // When every sheet was last swept. Zero until the first check, which is a sweep: it is what learns the tokens.
  let lastSweep = 0;
  let status = INITIAL_STATUS;
  let checking = false;
  // Set when the API turned a check away for something asking again will not fix. Checking stops rather than
  // showing "Reconnecting..." forever.
  let halted = false;
  let closed = false;
  let handle;
  let closeHandle;

  const setStatus = next => {
    const merged = {...status, ...next};

    if (merged.failed === status.failed && merged.stopped === status.stopped && merged.lastUpdate === status.lastUpdate) {
      return;
    }

    status = merged;
    listeners.slice().forEach(listener => listener.onStatus(status));
  };

  const schedule = interval => {
    clearTimeout(handle);
    handle = setTimeout(poll, interval);
  };

  const poll = async () => {
    // Nobody is looking at this page, so there is nothing to keep up to date. The checks stop until it is brought
    // back, which is what visibility, rather than focus, is asked about: a dashboard left up on a screen beside
    // someone working in another window is still being watched.
    if (closed || document.hidden || checking || halted) {
      return;
    }

    let interval = CHECK_INTERVAL;
    const sweeping = Date.now() - lastSweep >= SWEEP_INTERVAL;
    const askedSheetIds = sweeping
      ? sheetIds
      : watchedSheetIds.concat(retrySheetIds.filter(sheetId => watchedSheetIds.indexOf(sheetId) < 0));
    checking = true;

    try {
      if (askedSheetIds.length < 1) {
        // Every sheet is finished, so nothing is worth asking about until the next sweep.
        return;
      }

      // Every sheet being asked about, in as few requests as the API allows. The first check knows no tokens, which
      // is what loads the rows to begin with.
      const {changes, failedSheetIds, clientError} = await api.getSheetRowChangesForSheets(askedSheetIds, credentials);
      const versions = changes.map(change => ({sheetId: change.sheetId, version: change.version}));

      if (closed) {
        return;
      }

      // Whatever did come back is still used. The sheets that did not are asked about again next check, and the
      // sweep counts as done - otherwise one failing batch would have every sheet swept on every check.
      const partlyFailed = failedSheetIds.length > 0;
      retrySheetIds = failedSheetIds;

      if (partlyFailed) {
        interval = ERROR_INTERVAL;
      }

      if (clientError) {
        halted = true;
        setStatus({stopped: true});
      }

      if (sweeping) {
        lastSweep = Date.now();
      }

      const changedSheetIds = versions
        .filter(({sheetId, version}) => versionsBySheetId[sheetId] !== version)
        .map(({sheetId}) => sheetId);

      if (changedSheetIds.length < 1) {
        // Nothing has come in.
        const {lastUpdate} = status;
        setStatus({
          failed: partlyFailed,
          lastUpdate: lastUpdate && Date.now() - lastUpdate.getTime() < STATUS_INTERVAL ? lastUpdate : new Date()
        });
        return;
      }

      const pages = await Promise.all(changedSheetIds.map(sheetId =>
        api.getSheetRows(sheetId, filters, 0, PAGE_SIZE, 'dates.lastModifiedDate,desc', credentials)
          .then(page => ({sheetId, content: page.content}))
      ));

      if (closed) {
        return;
      }

      // Only recorded once the rows behind them are in hand, so a failed fetch is retried rather than skipped.
      versions.forEach(({sheetId, version}) => {
        versionsBySheetId[sheetId] = version;
      });

      pages.forEach(({sheetId, content}) => {
        pagesBySheetId[sheetId] = content;
      });

      listeners.slice().forEach(listener => listener.onRows(pages));
      setStatus({failed: partlyFailed, lastUpdate: new Date()});
    }
    catch (e) {
      if (!closed) {
        // The widgets keep showing the submissions they already have, and the check is tried again, less often.
        setStatus({failed: true});
        interval = ERROR_INTERVAL;
      }
    }
    finally {
      checking = false;

      if (!closed && !halted && !document.hidden) {
        schedule(interval);
      }
    }
  };

  // Check the moment the page is looked at again, so what is on screen is current rather than as stale as the page
  // was left.
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      poll();
    }
  };

  const close = () => {
    closed = true;
    clearTimeout(handle);
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    if (watchers[key] === watcher) {
      delete watchers[key];
    }
  };

  const watcher = {
    start() {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      poll();
    },

    subscribe(nextWatchedSheetIds, listener) {
      // A widget re-subscribing - because the sheets it watches closely moved - leaves and joins in the same render.
      // Closing is put off until then so the tokens and rows learned so far are not thrown away in between.
      clearTimeout(closeHandle);
      watchedSheetIds = nextWatchedSheetIds;
      listeners.push(listener);

      const cached = Object.keys(pagesBySheetId).map(sheetId => ({sheetId, content: pagesBySheetId[sheetId]}));

      if (cached.length > 0) {
        listener.onRows(cached);
      }

      // Always, so a widget that moves onto a new watcher does not keep the status of the one it left.
      listener.onStatus(status);

      return () => {
        const index = listeners.indexOf(listener);

        if (index >= 0) {
          listeners.splice(index, 1);
        }

        if (listeners.length < 1) {
          clearTimeout(closeHandle);
          closeHandle = setTimeout(close, 0);
        }
      };
    }
  };

  return watcher;
}
